/**
 * Hand-drawn SVG art for Hello DinDin (no emoji, no image files):
 * moment icons, a flat front view of the bump with footprints/glows,
 * and the sample Wrapped characters. All shapes are original.
 */
import type { MomentKind } from '../domain/moments';

let uid = 0;
const id = (p: string) => `${p}${++uid}`;

const FOOT = (x: number, y: number, s: number, rot = -12, fill = '#fff') =>
  `<g transform="translate(${x} ${y}) rotate(${rot}) scale(${s})" fill="${fill}">
    <ellipse cx="0" cy="4" rx="6.5" ry="9"/><circle cx="-5" cy="-8" r="2.2"/><circle cx="-1.6" cy="-10" r="2.1"/>
    <circle cx="1.8" cy="-10" r="1.9"/><circle cx="4.8" cy="-8.4" r="1.7"/><circle cx="7" cy="-5.6" r="1.5"/></g>`;

/** Round, soft icon for each moment kind. */
export function momentIcon(kind: MomentKind, size = 56): string {
  const bg = { first_kick: '#FCE3E6', partner_felt: '#E3ECF8', last_before_birth: '#ECE6F8' }[kind];
  const inner = {
    // a tiny footprint inside a heart
    first_kick: `<path d="M28 44 C 14 34 8 27 8 19 C 8 13 12 9 17.5 9 C 21.5 9 24.5 11 28 15 C 31.5 11 34.5 9 38.5 9 C 44 9 48 13 48 19 C 48 27 42 34 28 44 Z" fill="#F28FA6"/>
      ${FOOT(28, 25, 0.95, -10)}`,
    // a big hand with a little heart on the palm
    partner_felt: `<g fill="#E8B79C"><ellipse cx="28" cy="33" rx="13" ry="11"/>
      <rect x="15.5" y="14" width="5.6" height="18" rx="2.8"/><rect x="21.6" y="10" width="5.6" height="21" rx="2.8"/>
      <rect x="27.7" y="10.5" width="5.6" height="21" rx="2.8"/><rect x="33.8" y="14" width="5.4" height="18" rx="2.7"/>
      <rect x="38" y="26" width="5.4" height="13" rx="2.7" transform="rotate(-38 40.7 32.5)"/></g>
      <path d="M28 38 C 24 35 22.5 33.5 22.5 31.5 C 22.5 30 23.6 29 25 29 C 26.2 29 27.2 29.7 28 30.8 C 28.8 29.7 29.8 29 31 29 C 32.4 29 33.5 30 33.5 31.5 C 33.5 33.5 32 35 28 38 Z" fill="#F28FA6"/>`,
    // a sleepy crescent moon with a star
    last_before_birth: `<path d="M34 12 A 17 17 0 1 0 44 38 A 14 14 0 1 1 34 12 Z" fill="#F6D58A"/>
      <circle cx="24" cy="28" r="1.6" fill="#6B5850"/><path d="M27.5 33 Q 30 34.6 32.5 33" stroke="#6B5850" stroke-width="1.6" fill="none" stroke-linecap="round"/>
      <path d="M42 14 l1.6 3.4 3.6 .5 -2.6 2.5 .6 3.6 -3.2 -1.7 -3.2 1.7 .6 -3.6 -2.6 -2.5 3.6 -.5 z" fill="#fff"/>`
  }[kind];
  return `<svg width="${size}" height="${size}" viewBox="0 0 56 56" aria-hidden="true"><circle cx="28" cy="28" r="28" fill="${bg}"/>${inner}</svg>`;
}

export interface BumpGlow { x: number; y: number; warm?: boolean; strength?: number }

/**
 * Flat front view of the bump. x/y are -1..1 (y up). Used by the moment spot picker,
 * the paywall preview and later the Wrapped cards.
 */
export function bumpSvg(opts: { glows?: BumpGlow[]; foot?: { x: number; y: number } | null; width?: number; idAttr?: string }): string {
  const W = 200, H = 190, cx = 100, cy = 95, rx = 86, ry = 88;
  const g = id('b'), p = id('p'), h = id('h');
  const glows = (opts.glows ?? []).map(gl => `<ellipse cx="${cx + gl.x * rx}" cy="${cy - gl.y * ry}" rx="30" ry="26" fill="url(#${gl.warm === false ? h : p})" opacity="${Math.min(1, gl.strength ?? 0.35)}"/>`).join('');
  const foot = opts.foot ? FOOT(cx + opts.foot.x * rx, cy - opts.foot.y * ry, 1.25, -12) : '';
  return `<svg ${opts.idAttr ? `id="${opts.idAttr}"` : ''} class="bump-svg" width="${opts.width ?? W}" viewBox="0 0 ${W} ${H}" role="img">
    <defs>
      <radialGradient id="${g}" cx=".42" cy=".36" r=".8"><stop offset="0" stop-color="#FCE3D6"/><stop offset=".6" stop-color="#F3C7B2"/><stop offset="1" stop-color="#E3A98F"/></radialGradient>
      <radialGradient id="${p}" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="#FF8CAA"/><stop offset=".5" stop-color="#FFAFC6" stop-opacity=".45"/><stop offset="1" stop-color="#FFC9D6" stop-opacity="0"/></radialGradient>
      <radialGradient id="${h}" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="#FFD26E"/><stop offset=".5" stop-color="#FFE096" stop-opacity=".45"/><stop offset="1" stop-color="#FFE9B8" stop-opacity="0"/></radialGradient>
    </defs>
    <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="url(#${g})"/>
    ${glows}
    <ellipse cx="${cx + 4}" cy="${cy + 13}" rx="4.5" ry="3.5" fill="#C9826A" opacity=".8"/>
    <g style="filter: drop-shadow(0 1px 1.5px rgba(155,90,72,.45))">${foot}</g>
  </svg>`;
}

/** Convert a click inside a bumpSvg to bump coordinates, or null if outside the bump. */
export function bumpPoint(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } | null {
  const r = svg.getBoundingClientRect();
  const vx = ((clientX - r.left) / r.width) * 200, vy = ((clientY - r.top) / r.height) * 190;
  const x = (vx - 100) / 86, y = -(vy - 95) / 88;
  return x * x + y * y <= 1 ? { x, y } : null;
}

/** Sample Wrapped characters for the paywall teaser. */
export function owl(size = 44): string {
  return `<svg width="${size}" height="${size}" viewBox="-56 -60 112 116" aria-hidden="true">
    <ellipse cx="0" cy="8" rx="46" ry="50" fill="#F2C9A8"/><path d="M-40 -22 L-30 -52 L-14 -34 Z M40 -22 L30 -52 L14 -34 Z" fill="#E7B08C"/>
    <ellipse cx="0" cy="22" rx="28" ry="30" fill="#FCEBDD"/><circle cx="-18" cy="-8" r="17" fill="#fff"/><circle cx="18" cy="-8" r="17" fill="#fff"/>
    <circle cx="-16" cy="-6" r="8" fill="#3A2F2A"/><circle cx="20" cy="-6" r="8" fill="#3A2F2A"/><circle cx="-13" cy="-9" r="2.6" fill="#fff"/><circle cx="23" cy="-9" r="2.6" fill="#fff"/>
    <path d="M-6 6 L0 14 L6 6 Z" fill="#E9967A"/><ellipse cx="-30" cy="10" rx="6" ry="4" fill="#F4A9A4" opacity=".7"/><ellipse cx="30" cy="10" rx="6" ry="4" fill="#F4A9A4" opacity=".7"/></svg>`;
}
export function storybook(size = 44): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 56 56" aria-hidden="true">
    <path d="M8 14 C 16 11 23 12 28 16 L28 46 C 23 42 16 41 8 44 Z" fill="#FFF3E2" stroke="#E7B08C" stroke-width="2.4" stroke-linejoin="round"/>
    <path d="M48 14 C 40 11 33 12 28 16 L28 46 C 33 42 40 41 48 44 Z" fill="#FFF3E2" stroke="#E7B08C" stroke-width="2.4" stroke-linejoin="round"/>
    ${FOOT(18, 29, 0.8, -14, '#F28FA6')}
    <path d="M38 34 C 34 31 32 29 32 27 C 32 25.5 33.2 24.5 34.6 24.5 C 35.8 24.5 36.8 25.2 37.6 26.2 C 38.4 25.2 39.4 24.5 40.6 24.5 C 42 24.5 43.2 25.5 43.2 27 C 43.2 29 41.6 31 38 34 Z" fill="#F28FA6" transform="translate(-0.6 0)"/></svg>`;
}
export function filmStrip(size = 44): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 56 56" aria-hidden="true">
    <rect x="8" y="12" width="40" height="32" rx="7" fill="#B9A8E4"/>
    <g fill="#fff" opacity=".85"><rect x="11" y="15" width="4" height="4" rx="1"/><rect x="11" y="26" width="4" height="4" rx="1"/><rect x="11" y="37" width="4" height="4" rx="1"/>
    <rect x="41" y="15" width="4" height="4" rx="1"/><rect x="41" y="26" width="4" height="4" rx="1"/><rect x="41" y="37" width="4" height="4" rx="1"/></g>
    <path d="M24 21 L35 28 L24 35 Z" fill="#fff"/></svg>`;
}

/** More sample kick personalities (Wrapped teaser). */
export function rooster(size = 44): string {
  return `<svg width="${size}" height="${size}" viewBox="-56 -60 112 116" aria-hidden="true">
    <path d="M-8 -46 C -4 -58 6 -58 6 -48 C 12 -58 22 -54 16 -42 Z" fill="#F07A6E"/>
    <ellipse cx="0" cy="6" rx="42" ry="46" fill="#FFF4E6"/><ellipse cx="0" cy="26" rx="26" ry="22" fill="#FFE6C7"/>
    <circle cx="-14" cy="-10" r="6.5" fill="#3A2F2A"/><circle cx="14" cy="-10" r="6.5" fill="#3A2F2A"/><circle cx="-12" cy="-12" r="2" fill="#fff"/><circle cx="16" cy="-12" r="2" fill="#fff"/>
    <path d="M-9 2 L0 12 L9 2 Z" fill="#F5B041"/><path d="M-4 12 C -6 20 6 20 4 12 Z" fill="#F07A6E"/>
    <ellipse cx="-28" cy="4" rx="6" ry="4" fill="#F4A9A4" opacity=".7"/><ellipse cx="28" cy="4" rx="6" ry="4" fill="#F4A9A4" opacity=".7"/></svg>`;
}
export function cat(size = 44): string {
  return `<svg width="${size}" height="${size}" viewBox="-56 -60 112 116" aria-hidden="true">
    <path d="M-40 -14 L-34 -50 L-12 -32 Z M40 -14 L34 -50 L12 -32 Z" fill="#E9C6A3"/><path d="M-34 -22 L-31 -42 L-19 -32 Z M34 -22 L31 -42 L19 -32 Z" fill="#F4A9A4"/>
    <ellipse cx="0" cy="6" rx="46" ry="42" fill="#F3D6B8"/>
    <path d="M-24 -4 Q -16 4 -8 -4 M8 -4 Q 16 4 24 -4" stroke="#3A2F2A" stroke-width="4" fill="none" stroke-linecap="round"/>
    <path d="M-4 10 L0 14 L4 10 Z" fill="#E9967A"/><path d="M0 14 Q -6 20 -11 17 M0 14 Q 6 20 11 17" stroke="#3A2F2A" stroke-width="2.4" fill="none" stroke-linecap="round"/>
    <ellipse cx="-30" cy="12" rx="7" ry="4.5" fill="#F4A9A4" opacity=".7"/><ellipse cx="30" cy="12" rx="7" ry="4.5" fill="#F4A9A4" opacity=".7"/></svg>`;
}
